/**
 * Exchange Rate Service
 * 
 * Provides functions for fetching historical exchange rate data from the backend API
 * with client-side caching to reduce API calls and improve performance.
 */

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const cache = new Map();

/**
 * Fetches historical exchange rates for a given currency pair and time range.
 * Implements client-side caching with 5-minute TTL to reduce API load.
 * 
 * @param {string} currencyFrom - Base currency code (e.g., "BTC")
 * @param {string} currencyTo - Quote currency code (e.g., "EUR")
 * @param {string} range - Time range: "24h", "7d", "30d", or "1y"
 * @returns {Promise<Object>} Historical exchange rate response with data points
 * @throws {Error} If the API request fails
 */
export const fetchHistoricalExchangeRates = async (currencyFrom, currencyTo, range) => {
  const cacheKey = `${currencyFrom}-${currencyTo}-${range}`;
  const cached = cache.get(cacheKey);
  
  // Check if cached data exists and is still valid
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  
  // Build API URL with query parameters
  const url = new URL("/exchange_rates/historical", process.env.GATSBY_API_URL);
  url.searchParams.append("currencyFrom", currencyFrom);
  url.searchParams.append("currencyTo", currencyTo);
  url.searchParams.append("range", range);
  
  try {
    const response = await fetch(url, { method: "GET" });
    
    if (!response.ok) {
      // Handle different HTTP error codes
      if (response.status === 400) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Invalid request parameters");
      } else if (response.status === 503) {
        throw new Error("Service temporarily unavailable. Please try again later.");
      } else if (response.status === 504) {
        throw new Error("Request timeout. Please try again.");
      } else {
        throw new Error("Failed to fetch historical exchange rates");
      }
    }
    
    const data = await response.json();
    
    // Cache the successful response
    cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    
    return data;
  } catch (error) {
    // Re-throw with appropriate error object
    if (error.message) {
      throw error;
    } else {
      throw new Error("Network error. Please check your internet connection.");
    }
  }
};

/**
 * Fetches the latest (current) exchange rates from the backend API.
 * 
 * @returns {Promise<Array>} Array of latest exchange rate objects
 * @throws {Error} If the API request fails
 */
export const fetchLatestExchangeRates = async () => {
  const url = new URL("/exchange_rates/latest", process.env.GATSBY_API_URL);
  
  try {
    const response = await fetch(url, { method: "GET" });
    
    if (!response.ok) {
      throw new Error("Failed to fetch latest exchange rates");
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    if (error.message) {
      throw error;
    } else {
      throw new Error("Network error. Please check your internet connection.");
    }
  }
};

/**
 * Validates that the latest historical data point is consistent with the current exchange rate.
 * Checks if the values are within 5% of each other.
 * 
 * @param {number} historicalValue - The latest value from historical data
 * @param {number} currentValue - The current exchange rate value
 * @returns {Object} Validation result with isValid flag and percentageDiff
 */
export const validateDataConsistency = (historicalValue, currentValue) => {
  if (!historicalValue || !currentValue) {
    return { isValid: false, percentageDiff: null, reason: "missing_data" };
  }
  
  const percentageDiff = Math.abs((historicalValue - currentValue) / currentValue) * 100;
  const isValid = percentageDiff <= 5;
  
  return { isValid, percentageDiff };
};

/**
 * Clears the cache for a specific currency pair and range, or clears all cache if no parameters provided.
 * Useful for forcing a fresh data fetch.
 * 
 * @param {string} [currencyFrom] - Optional base currency code
 * @param {string} [currencyTo] - Optional quote currency code
 * @param {string} [range] - Optional time range
 */
export const clearCache = (currencyFrom, currencyTo, range) => {
  if (currencyFrom && currencyTo && range) {
    const cacheKey = `${currencyFrom}-${currencyTo}-${range}`;
    cache.delete(cacheKey);
  } else {
    cache.clear();
  }
};
